import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Container, Card, CardContent, Typography, CircularProgress, Paper,
  Stack, Chip, Tooltip, Divider, TextField, InputAdornment, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TablePagination,
  Accordion, AccordionSummary, AccordionDetails, Alert
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Search as SearchIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import dayjs from "dayjs";
import api from "../api/api";


const brand = {
  pageBg: "#f7f9fc",
  cardBg: "#ffffff",
  border: "#e5e7eb",
  text: "#0f172a",
  sub: "#6b7280",
  blue: "#005695",
  thead: "#f2f4f8",
};

function CompanyChip({ company }) {
  if (!company) return <Chip label="—" size="small" />;
  const label = company.name || company.crm_company_id || company.id || "Company";
  return (
    <Chip label={label} size="small" sx={{ bgcolor: "rgba(0,86,149,.08)", color: brand.blue, fontWeight: 600 }} />
  );
}

function AnswerTable({ answers }) {
  if (!answers?.length) {
    return <Typography variant="body2" sx={{ color: brand.sub }}>No answers recorded.</Typography>;
  }
  return (
    <Paper variant="outlined" sx={{ borderColor: brand.border }}>
      <Table size="small" stickyHeader>
        <TableHead sx={{ background: brand.thead }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 320 }}>Answer</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {answers.map((a, idx) => (
            <TableRow key={`${a.question_id}-${idx}`}>
              <TableCell sx={{ verticalAlign: "top" }}>
                {a.question_text || `#${a.question_id}`}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                {a.is_mcq
                  ? (a.selected_answer_text || <em style={{ color: brand.sub }}>—</em>)
                  : (a.answer_text || <em style={{ color: brand.sub }}>—</em>)
                }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function AdminSurveyFeedbackPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRpp] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [crmId, setCrmId] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        search: search || undefined,
        company_id: companyId || undefined,
        crm_company_id: crmId || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const res = await api.get("/admin/survey-results2", { params /* ensure auth cookie/token */ });
      setRows(res?.data?.data || []);
      setTotal(Number(res?.data?.total || 0));
    } catch (e) {
      console.error("Failed to load survey results", e);
      const msg = e?.response?.data?.message || e?.message || "Request failed";
      setError(`${msg} (${e?.response?.status || ""})`);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const onSearch = () => {
    setPage(0);
    fetchRows();
  };

  const metrics = useMemo(() => ({
    countPage: rows.length,
    companiesPage: new Set(rows.map(r => r.company?.id || r.company?.crm_company_id || r.company?.name)).size,
  }), [rows]);

  return (
    <Box sx={{ minHeight: "100vh", background: brand.pageBg }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${brand.blue} 0%, #1976d2 100%)`,
        color: "#fff", py: 3, mb: 4, boxShadow: "0 6px 24px rgba(0,0,0,.15)"
      }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Feedback Results</Typography>
          <Typography sx={{ opacity: .9 }}>Company information & submission time with full Q&A</Typography>
          <Stack direction="row" spacing={1.2} sx={{ mt: 2 }} useFlexGap>
            <Chip label={`Results (page): ${metrics.countPage}`} size="small" sx={{ bgcolor: "rgba(255,255,255,.16)", color: "#fff" }} />
            <Chip label={`Companies (page): ${metrics.companiesPage}`} size="small" sx={{ bgcolor: "rgba(255,255,255,.16)", color: "#fff" }} />
            <Chip label={`Total (server): ${total}`} size="small" sx={{ bgcolor: "rgba(255,255,255,.16)", color: "#fff" }} />
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Paper sx={{ p: 2, mb: 2, border: `1px solid ${brand.border}` }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap alignItems="center">
            <TextField
              size="small"
              label="Search (company, name, email)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: brand.sub }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField size="small" label="Company ID" value={companyId} onChange={(e) => setCompanyId(e.target.value)} sx={{ width: 140 }} />
            <TextField size="small" label="CRM Company GUID" value={crmId} onChange={(e) => setCrmId(e.target.value)} sx={{ width: 280 }} />
            <TextField size="small" label="From" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ width: 170 }} InputLabelProps={{ shrink: true }} />
            <TextField size="small" label="To" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ width: 170 }} InputLabelProps={{ shrink: true }} />
            <IconButton onClick={onSearch} title="Apply filters"><RefreshIcon /></IconButton>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error} — check Network tab and `storage/logs/laravel.log` for `AdminSurvey.index.*`.
          </Alert>
        )}

        <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 8px 26px rgba(0,0,0,.06)" }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <CircularProgress />
                <Typography sx={{ mt: 1.5, color: "text.secondary" }}>Loading feedback…</Typography>
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ py: 4 }}>
                <Typography align="center" sx={{ color: brand.sub }}>No feedback found.</Typography>
              </Box>
            ) : (
              <>
                <Paper sx={{ borderRadius: 0 }}>
                  <Table stickyHeader>
                    <TableHead sx={{ background: brand.thead }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Submitted At</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 120 }} align="center">Questions</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 120 }} align="center">Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.id} hover sx={{ "& td": { borderBottomColor: brand.border } }}>
                          <TableCell>
                            <Tooltip title={r.submitted_at}>
                              <Typography sx={{ fontWeight: 700 }}>
                                {r.submitted_at ? dayjs(r.submitted_at).format("DD MMM YYYY, HH:mm") : "—"}
                              </Typography>
                            </Tooltip>
                            <Typography variant="caption" sx={{ color: brand.sub }}>
                              ID #{r.id}{r.survey_id ? ` · Survey ${r.survey_id}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CompanyChip company={r.company} />
                              <Typography variant="body2">{r.company?.name || "—"}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 700 }}>{r.user?.name || "—"}</Typography>
                            <Typography variant="caption" sx={{ color: brand.sub }}>{r.user?.email || "—"}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={r.counts?.questions ?? 0} size="small" />
                          </TableCell>
                          <TableCell align="center" sx={{ p: 0 }}>
                            <Accordion sx={{ boxShadow: "none" }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography sx={{ fontWeight: 600 }}>View</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <AnswerTable answers={r.answers} />
                              </AccordionDetails>
                            </Accordion>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>

                <Divider />

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_e, p) => setPage(p)}
                  onRowsPerPageChange={(e) => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
                  sx={{ background: "#fafafa", borderTop: `1px solid ${brand.border}` }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
